import { AppApiGateway } from '@repo/api/web/AppApiGateway'
import {
  type ReviewsListByProductRequestQuery,
  type ReviewsListMineRequestQuery,
  ShowcaseHideAuthorNameRequest,
  type ShowcaseCreateReviewRequest,
  type ShowcaseUpdateReviewRequest,
} from '@repo/api/web/models'
import { inject, injectable } from 'inversify'

import { CreateResource } from '#Resources/Models'
import { ResourceService } from '#Resources/services'

/** Showcase API отзывов текущей витрины (RFC-008 It.1). */
@injectable()
export class ReviewsService {
  protected appConfig: ReturnType<typeof useAppConfig>

  constructor(
    @inject(AppApiGateway)
    readonly api: AppApiGateway,
    @inject(ResourceService)
    readonly resources: ResourceService,
  ) {
    this.appConfig = useAppConfig()
  }

  /** Настройки отзывов витрины. На SSR их грузит плагин reviewsConfigLoader. */
  config() {
    return this.api.reviewsConfig(this.appConfig.host)
  }

  listByProduct(productId: number, query: ReviewsListByProductRequestQuery) {
    return this.api.reviewsListByProduct(productId, this.appConfig.host, query)
  }

  summary(productId: number) {
    return this.api.reviewsSummary(productId, this.appConfig.host)
  }

  create(productId: number, model: ShowcaseCreateReviewRequest) {
    return this.api.reviewsCreate(productId, this.appConfig.host, model)
  }

  update(reviewId: number, model: ShowcaseUpdateReviewRequest) {
    return this.api.reviewsUpdate(reviewId, this.appConfig.host, model)
  }

  setHideAuthorName(reviewId: number, hideAuthorName: boolean) {
    return this.api.reviewsSetHideAuthorName(reviewId, this.appConfig.host, ShowcaseHideAuthorNameRequest.createLazy({ hideAuthorName }))
  }

  remove(reviewId: number) {
    return this.api.reviewsDelete(reviewId, this.appConfig.host)
  }

  listMine(query: ReviewsListMineRequestQuery) {
    return this.api.reviewsListMine(this.appConfig.host, query)
  }

  listReviewable() {
    return this.api.reviewsListReviewable(this.appConfig.host)
  }

  /** Загрузка фото покупателем через общий ResourceService; возвращает id ресурса. */
  async uploadPhoto(file: File): Promise<string> {
    const id = await this.resources.create(
      CreateResource.createLazy({ file, name: file.name, ignoreImageCheck: false }),
    )
    if (!id) throw new Error('Сервер не вернул идентификатор загруженного фото')
    return id
  }

  /** Подписи enum'ов из `[EnumDisplayName]` бэкенда. */
  enums() {
    return this.api.dictionariesGetEnums(this.appConfig.host)
  }
}
